import axios from 'axios';

// SECURITY: Ensure credentials (session cookies) are always sent with requests
// This is critical for user attribution in audit logs
axios.defaults.withCredentials = true;

/**
 * Submit a contact to a lender with optional file attachments
 * 
 * @param {Object} contactData - Contact information
 * @param {Object} businessData - Business opportunity data
 * @param {string} lenderId - ID of the lender to submit to
 * @param {File[]} bankFiles - Array of bank statement files
 * @param {File[]} appFiles - Array of application document files
 * @param {string} [financingType='working-capital'] - Type of financing ('working-capital' or 'equipment-financing')
 * @param {string} [submissionType='api'] - Type of submission ('api' or 'email')
 * @param {Object} [additionalFiles={}] - Additional files for specific financing types
 * @param {File[]} [additionalFiles.quoteFiles] - Array of equipment quote files for equipment financing
 * @param {File[]} [additionalFiles.creditFiles] - Array of credit files for equipment financing
 * @returns {Promise<Object>} Response from the lender API
 */
export const submitToLender = async (contactData, businessData, lenderId, bankFiles = [], appFiles = [], financingType = 'working-capital', submissionType = 'api', additionalFiles = {}, dealNotes = '') => {
  try {
    // Create form data for file upload
    const formData = new FormData();
    
    // Add contact and business data
    formData.append('contactData', JSON.stringify(contactData));
    formData.append('businessData', JSON.stringify(businessData));
    formData.append('lenderId', lenderId);
    formData.append('financingType', financingType);
    formData.append('submissionType', submissionType);
    formData.append('dealNotes', dealNotes);
    
    // Create a structure for Salesforce files
    const salesforceFiles = {
      bankFiles: [],
      appFiles: [],
      quoteFiles: [],
      creditFiles: []
    };
    
    // Track if we have any Salesforce files
    let hasSalesforceFiles = false;
    
    // Add bank statement files
    for (const file of bankFiles) {
      if (file.salesforceFile) {
        // If it's a Salesforce file, add to the salesforceFiles structure
        salesforceFiles.bankFiles.push({
          name: file.name,
          salesforceId: file.salesforceId,
          type: file.type || file.mimetype,
          size: file.size,
          source: file.source || 'ContentDocument'
        });
        hasSalesforceFiles = true;
      } else {
        // Regular file, add directly to FormData
        formData.append('bankFiles', file);
      }
    }
    
    // Add application document files
    for (const file of appFiles) {
      if (file.salesforceFile) {
        // If it's a Salesforce file, add to the salesforceFiles structure
        salesforceFiles.appFiles.push({
          name: file.name,
          salesforceId: file.salesforceId,
          type: file.type || file.mimetype,
          size: file.size,
          source: file.source || 'ContentDocument'
        });
        hasSalesforceFiles = true;
      } else {
        // Regular file, add directly to FormData
        formData.append('appFiles', file);
      }
    }
    
    // Add equipment quote files if provided (for equipment financing)
    if (additionalFiles.quoteFiles && additionalFiles.quoteFiles.length > 0) {
      for (const file of additionalFiles.quoteFiles) {
        if (file.salesforceFile) {
          // If it's a Salesforce file, add to the salesforceFiles structure
          salesforceFiles.quoteFiles.push({
            name: file.name,
            salesforceId: file.salesforceId,
            type: file.type || file.mimetype,
            size: file.size,
            source: file.source || 'ContentDocument'
          });
          hasSalesforceFiles = true;
        } else {
          // Regular file, add directly to FormData
          formData.append('quoteFiles', file);
        }
      }
    }
    
    // Add credit files if provided (for equipment financing)
    if (additionalFiles.creditFiles && additionalFiles.creditFiles.length > 0) {
      for (const file of additionalFiles.creditFiles) {
        if (file.salesforceFile) {
          // If it's a Salesforce file, add to the salesforceFiles structure
          salesforceFiles.creditFiles.push({
            name: file.name,
            salesforceId: file.salesforceId,
            type: file.type || file.mimetype,
            size: file.size,
            source: file.source || 'ContentDocument'
          });
          hasSalesforceFiles = true;
        } else {
          // Regular file, add directly to FormData
          formData.append('creditFiles', file);
        }
      }
    }
    
    // If we have any Salesforce files, add them to FormData as JSON
    if (hasSalesforceFiles) {
      console.log('Adding Salesforce files to submission:', salesforceFiles);
      formData.append('salesforceFiles', JSON.stringify(salesforceFiles));
    }
    
    // Submit to the lender
    // SECURITY: withCredentials is required to send session cookies for user attribution
    const response = await axios.post('/api/lenders/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true  // CRITICAL: Send session cookies for authentication
    });
    
    return response.data;
  } catch (error) {
    console.error('Lender submission error:', error);
    
    // Create a detailed error object
    const errorObj = new Error();
    
    if (error.response) {
      // Extract response status and data
      errorObj.response = {
        status: error.response.status,
        data: error.response.data || {}
      };
      
      // Set message based on response data or status
      if (error.response.data && error.response.data.message) {
        errorObj.message = error.response.data.message;
      } else {
        errorObj.message = `Server returned error ${error.response.status}`;
      }
    } else if (error.request) {
      // No response received
      errorObj.message = 'No response received from server';
      errorObj.request = error.request;
    } else {
      // Set message for other errors
      errorObj.message = error.message || 'Unknown error occurred';
    }
    
    throw errorObj;
  }
};

/**
 * Get lender match settings
 * 
 * @returns {Promise<Object>} Lender match settings
 */
export const getLenderMatchSettings = async () => {
  try {
    const response = await axios.get('/api/lenders/match/settings');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Toggle lender match enabled status
 * 
 * @param {boolean} enabled - Whether lender match should be enabled 
 * @returns {Promise<Object>} Response with updated enabled status
 */
export const toggleLenderMatch = async (enabled) => {
  try {
    const response = await axios.put('/api/lenders/match/enabled', { enabled });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get all available fields for lender criteria
 * 
 * @returns {Promise<Array>} Array of available fields
 */
export const getAvailableFields = async () => {
  try {
    const response = await axios.get('/api/lenders/match/available-fields');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update criteria for a specific lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {string} name - Name of the lender
 * @param {Array} criteria - Array of criteria objects
 * @returns {Promise<Object>} Response with updated lender criteria
 */
export const updateLenderCriteria = async (lenderId, name, criteria) => {
  try {
    const response = await axios.post('/api/lenders/match/lender-criteria', {
      lenderId,
      name,
      criteria
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Delete a specific criterion for a lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {number} criterionIndex - Index of the criterion to delete
 * @returns {Promise<Object>} Response with deletion confirmation
 */
export const deleteCriterion = async (lenderId, criterionIndex) => {
  try {
    const response = await axios.delete(`/api/lenders/match/lender-criteria/${lenderId}/${criterionIndex}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Calculate lender match scores for a contact
 * 
 * @param {string} contactId - ID of the contact
 * @returns {Promise<Object>} Response with match scores
 */
export const calculateMatchScores = async (contactId) => {
  try {
    const response = await axios.get(`/api/lenders/match/calculate/${contactId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get active lenders available in the system
 * 
 * @param {string} [financingType='all'] - Type of financing ('all', 'working-capital', or 'equipment-financing')
 * @returns {Promise<Array>} Array of active lenders with id and name
 */
export const getActiveLenders = async (financingType = 'all') => {
  try {
    const response = await axios.get(`/api/lenders/active-lenders?financingType=${financingType}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching active lenders:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Sync active lenders with the backend
 * 
 * @param {Array} lenders - Array of lender objects with id and name
 * @param {string} [financingType='working-capital'] - Type of financing
 * @returns {Promise<Object>} Response from the lender API
 */
export const syncActiveLenders = async (lenders, financingType = 'working-capital') => {
  try {
    const response = await axios.post('/api/lenders/active-lenders/sync', { 
      lenders,
      financingType
    });
    return response.data;
  } catch (error) {
    console.error('Error syncing lenders with backend:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Reset active lenders in the backend by syncing both equipment and working capital lenders
 * 
 * @param {Array} equipmentLenders - Array of equipment financing lenders
 * @param {Array} workingCapitalLenders - Array of working capital lenders
 * @returns {Promise<Object>} Response from the lender API
 */
export const resetActiveLenders = async (equipmentLenders, workingCapitalLenders) => {
  try {
    console.log('Resetting active lenders in the backend...');
    
    // First sync working capital lenders
    console.log(`Syncing ${workingCapitalLenders.length} working capital lenders...`);
    await syncActiveLenders(
      workingCapitalLenders.filter(l => !l.id.startsWith('//')),
      'working-capital'
    );
    
    // Then sync equipment financing lenders
    console.log(`Syncing ${equipmentLenders.length} equipment financing lenders...`);
    await syncActiveLenders(
      equipmentLenders.filter(l => !l.id.startsWith('//')),
      'equipment-financing'
    );
    
    console.log('All lenders synced successfully');
    
    return { 
      success: true, 
      message: 'Lenders reset successfully',
      workingCapitalCount: workingCapitalLenders.filter(l => !l.id.startsWith('//')).length,
      equipmentCount: equipmentLenders.filter(l => !l.id.startsWith('//')).length
    };
  } catch (error) {
    console.error('Error resetting active lenders:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update lender tier assignments
 * 
 * @param {Array} lenderTiers - Array of objects with lenderId and tier
 * @returns {Promise<Object>} Response from the lender API
 */
export const updateLenderTiers = async (lenderTiers) => {
  try {
    const response = await axios.put('/api/lenders/tiers', { lenderTiers });
    return response.data;
  } catch (error) {
    console.error('Error updating lender tiers:', error);
    throw error.response ? error.response.data : error;
  }
};

// Cache to prevent duplicate requests for lender settings
const lenderSettingsRequestCache = {
  lastRequest: null,
  inProgress: false,
  timestamp: 0,
  debounceTime: 2000 // 2 seconds
};

/**
 * Fetch all lender settings
 * 
 * @returns {Promise<Array>} Array of lender settings
 */
export const getLenderSettings = async () => {
  try {
    const response = await axios.get('/api/lenders/settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching lender settings:', error);
    throw error;
  }
};

/**
 * Save lender settings
 * 
 * @param {Array} settings - Array of lender settings objects
 * @returns {Promise<Array>} Updated array of lender settings with unique IDs
 */
export const saveLenderSettings = async (settings) => {
  try {
    // Check if we have a request in progress or if the last request was too recent
    const now = Date.now();
    if (lenderSettingsRequestCache.inProgress || (now - lenderSettingsRequestCache.timestamp < lenderSettingsRequestCache.debounceTime && 
        JSON.stringify(settings) === lenderSettingsRequestCache.lastRequest)) {
      console.log('Preventing duplicate lender settings save request');
      return []; // Return empty array to prevent state updates
    }
    
    // Mark request as in progress
    lenderSettingsRequestCache.inProgress = true;
    lenderSettingsRequestCache.lastRequest = JSON.stringify(settings);
    lenderSettingsRequestCache.timestamp = now;
    
    const response = await axios.post('/api/lenders/settings', settings);
    
    // Reset in progress flag
    lenderSettingsRequestCache.inProgress = false;
    
    return response.data;
  } catch (error) {
    // Reset in progress flag on error
    lenderSettingsRequestCache.inProgress = false;
    
    console.error('Error saving lender settings:', error);
    throw error;
  }
};

/**
 * Delete a specific lender from the database
 * 
 * @param {string} lenderId - The string ID of the lender to delete
 * @returns {Promise<Object>} Response from the delete operation
 */
export const deleteLenderSetting = async (lenderId) => {
  try {
    console.log(`Deleting lender: ${lenderId}`);
    const response = await axios.delete(`/api/lenders/settings/${lenderId}`);
    console.log(`Lender ${lenderId} deleted successfully:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error deleting lender ${lenderId}:`, error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get active lender settings
 * 
 * @param {string} [financingType] - Optional financing type filter
 * @returns {Promise<Array>} Array of active lender settings
 */
export const getActiveLenderSettings = async (financingType = null) => {
  try {
    const url = financingType 
      ? `/api/lenders/settings/active/${financingType}`
      : '/api/lenders/settings/active';
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching active lender settings:', error);
    throw error;
  }
};

/**
 * Get watermark setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<boolean>} Whether watermarking is enabled for this lender
 */
export const getLenderWatermarkSetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lenders/settings/lender/${lenderId}/watermark`);
    return response.data.watermarkEnabled;
  } catch (error) {
    console.error(`Error fetching watermark setting for lender ${lenderId}:`, error);
    return false; // Default to false on error
  }
};

/**
 * Get contact info scramble setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<boolean>} Whether contact info scrambling is enabled for this lender
 */
export const getLenderScrambleSetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lenders/settings/lender/${lenderId}/scramble`);
    return response.data.contactInfoScramble;
  } catch (error) {
    console.error(`Error fetching contact info scramble setting for lender ${lenderId}:`, error);
    return false; // Default to false on error
  }
};

/**
 * Get general email settings including mandatory CC configuration
 * 
 * @returns {Promise<Object>} Email settings object
 */
export const getEmailSettings = async () => {
  try {
    console.log('Fetching email settings...');
    const response = await axios.get('/api/lenders/email-settings');
    console.log('Email settings fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching email settings:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update general email settings including mandatory CC configuration
 * 
 * @param {Object} settings - Email settings object with mandatoryCcEnabled, juniorAeField, seniorAeField
 * @returns {Promise<Object>} Updated email settings
 */
export const updateEmailSettings = async (settings) => {
  try {
    console.log('Updating email settings:', settings);
    const response = await axios.put('/api/lenders/email-settings', settings);
    console.log('Email settings updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating email settings:', error);
    throw error.response ? error.response.data : error;
  }
}; 